import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { Editor } from '@monaco-editor/react';
import ContractForm from '../components/ContractForm';
import AnalysisResults from '../components/AnalysisResults';
import ScannerToolbar from '../components/ScannerToolbar';
import IssueDetails from '../components/IssueDetails';
import { API_BASE } from '../App';

const ContractAnalysis: React.FC = () => {
  const [contract, setContract] = React.useState('');
  const [results, setResults] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedIssue, setSelectedIssue] = React.useState<any | null>(null);

  const handleAnalyze = async (contractCode: string) => {
    setError(null);
    if (!contractCode || contractCode.trim().length === 0) {
      setError('Contract code is empty. Please paste a Solidity contract before analyzing.');
      return;
    }

    setLoading(true);
    setResults(null);
    try {
      const response = await fetch(`${API_BASE}/api/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contract: contractCode }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server returned ${response.status}: ${text}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err: any) {
      console.error('Analysis failed:', err);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <ScannerToolbar
        onLoadSample={(txt) => setContract(txt)}
        onExport={() => {
          if (results) {
            import('../utils/reportGenerator').then(({ generateHTMLReport }) => {
              const reportData = {
                contract,
                results: {
                  staticAnalysis: results.staticAnalysis || [],
                  symbolicExecution: results.symbolicExecution || []
                },
                timestamp: new Date().toISOString()
              };

              const htmlContent = generateHTMLReport(reportData);
              const blob = new Blob([htmlContent], { type: 'text/html' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'vulnerability-report.html';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            });
          }
        }}
      />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Contract Analysis
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Smart Contract Code
            </Typography>
            <Editor
              height="60vh"
              defaultLanguage="solidity"
              value={contract}
              onChange={(value) => setContract(value || '')}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
              }}
            />
            <Box sx={{ mt: 2 }}>
              <ContractForm onSubmit={() => handleAnalyze(contract)} disabled={loading} loading={loading} />
            </Box>
            {error && (
              <Box sx={{ mt: 2 }}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, minHeight: '70vh' }}>
            <Typography variant="h6" gutterBottom>
              Analysis Results
            </Typography>
            {results && <AnalysisResults results={results} onSelectIssue={(issue) => setSelectedIssue(issue)} />}
            <IssueDetails open={Boolean(selectedIssue)} issue={selectedIssue} onClose={() => setSelectedIssue(null)} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractAnalysis;